import React, { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { createChart } from "lightweight-charts";
import {
    Box, Button, Container, Paper, Grid, Typography,
    List, ListItem, ListItemText, ListItemAvatar, Avatar, Divider, IconButton
} from "@mui/material";
import { useTheme } from '@mui/material';

import Header from "../../components/Header";
import LightweightChartv1 from "../../components/LightweightChart v1";
import dayjs from 'dayjs';

const generateData = (data, date_columm = 'datetime', field, haveTime = false) => {
    const timeZoneOffset = 7 * 60 * 60 * 1000;
    const cdata = data.map(d => {
        let time = timeToTz(d[date_columm], 'Asia/Bangkok')

        const dd = new Date(d[date_columm])
        const year = dd.getFullYear();
        const month = String(dd.getMonth() + 1).padStart(2, '0'); // Add 1 because months are zero-indexed
        const day = String(dd.getDate()).padStart(2, '0');
        const hour = String(dd.getHours()).padStart(2, '0');
        const minute = String(dd.getMinutes()).padStart(2, '0');
        const second = String(dd.getSeconds()).padStart(2, '0');

        // let time2 = (new Date(d[date_columm]).getTime() + timeZoneOffset) / 1000
        let time2 = `${year}-${month}-${day}`;
        if (haveTime) {
            time2 = Math.floor((dd.getTime() + timeZoneOffset) / 1000);
        }

        return { time: time2, value: parseFloat(d[field]) || 0 }
    });
    return cdata;
}
function timeToTz(originalTime, timeZone) {
    const zonedDate = new Date(new Date(originalTime).toLocaleString('en-US', { timeZone }));
    // const options = {
    //     timeZone: timeZone,
    //     hour: 'numeric',
    //     minute: 'numeric',
    //     second: 'numeric',
    //     hour12: false,
    // };
    // const formatter = new Intl.DateTimeFormat('en-US', options);
    return zonedDate.getTime() / 1000;
}

const FullChart = () => {
    const theme = useTheme();
    const { pageType, market, sector, date } = useParams();
    const chartContainerRef = useRef(null);
    const location = useLocation();
    const [storedSymbols, setStoredSymbols] = useState([]);

    // ดึงข้อมูล Base64 จาก query parameters
    const queryParams = new URLSearchParams(location.search);
    const base64Data = queryParams.get("data");

    // แปลงจาก Base64 กลับเป็น JSON
    const allData = base64Data ? JSON.parse(atob(base64Data)) : [];


    const [chartData, setChartData] = useState([]);
    const [chartDataPrice, setChartDataPrice] = useState([]);
    const [chartTitle, setChartTitle] = useState([]);
    const [isIntraday, setIsIntraday] = useState([]);

    const [selectedOption, setSelectedOption] = useState('SET');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchChartData = async () => {
            setLoading(true);
            const decodedSector = decodeURIComponent(sector);
            setChartTitle(`${decodedSector}`);
            
            let apiUrl = '';
            let params = {};
            let isIntraday = false;

            // 2. แยก Logic ตาม pageType ที่ได้จาก URL
            if (pageType === 'daily') {
                apiUrl = `${window.config.base_api_url}/sector_rotation2`;
                params = { market, sector };
                setIsIntraday(false);
            } else if (pageType === 'intraday') {
                apiUrl = `${window.config.base_api_url}/sector_rotation/intraday`;
                params = { market, sector, date };
                setIsIntraday(true);
            } else {
                console.error("Unknown page type:", pageType);
                setLoading(false);
                return;
            }

            try {
                const url = `${apiUrl}?${new URLSearchParams(params)}`;
                const response = await fetch(url);
                if (!response.ok) throw new Error('Network response was not ok');

                const apiData = await response.json();

                if (apiData && apiData.data) {
                    // เราสามารถใช้ isIntraday ที่มาจาก state ได้โดยตรง แต่เพื่อความแน่นอน
                    // เราส่งค่า boolean จาก pageType ไปเลยจะดีกว่า
                    const isDataIntraday = pageType === 'intraday';
                    const flowData = generateData(apiData.data.data || [], 'datetime', 'cumsum', isDataIntraday);
                    const priceData = generateData(apiData.data.price || [], 'datetime', 'value', isDataIntraday);
                    const symbols = apiData.data.symbols || [];

                    setChartData(flowData);
                    setChartDataPrice(priceData);
                    setStoredSymbols(symbols);
                }
            } catch (error) {
                console.error("Failed to fetch initial chart data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchChartData();
    }, [pageType, market, sector, date]);

    const commonOptions = isIntraday ? {
        crosshair: {
            mode: 0
        },
        timeScale: {
            visible: true,
            timeVisible: true,
            secondsVisible: true,
        },
        localization: {
            locale: "en-US"
        },
        layout: {
            attributionLogo: false,
            background: { type: 'solid', color: theme.palette.background.default },
            textColor: theme.palette.text.primary,
          },
        grid: {
             horzLines: { color: theme.palette.divider },
             vertLines: { color: theme.palette.divider },
          },
         
        title: {
            color: 'white',
        },
        rightPriceScale: {
            visible: false,
        },
        leftPriceScale: {
            visible: false,
        },
        localization: {
            locale: 'th-TH',
        },

    } : {
        crosshair: {
            mode: 0
        },
        timeScale: {
            visible: true,
        },
        localization: {
            locale: "en-US"
        },
        layout: {
            attributionLogo: false,
            background: { type: 'solid', color: theme.palette.background.default },
            textColor: theme.palette.text.primary,
          },
        title: {
            text: chartTitle, // ชื่อกราฟ
            color: '#ffffff', // สีตัวอักษรเป็นขาว
            font: {
                size: 16, // ขนาดตัวอักษร
                weight: 'bold', // ขนาดตัวหนา
            },
        },
        grid: {
            horzLines: { color: theme.palette.divider },
            vertLines: { color: theme.palette.divider },
         },
        rightPriceScale: {
            visible: false,
        },
        leftPriceScale: {
            visible: false,
        },
    };

    const chartRefs = useRef([]);
    const chartContainerRefs = useRef([]);
    const [isReady, setIsReady] = useState(false);
    const [seriesOptions, setSeriesOptions] = useState([]);

    const content = document.getElementsByClassName('content')[0];
    const resizeHandler = () => {
        chartRefs.current.forEach((chartRef, index) => {
            if (chartRef) {
                const chart = chartRef.getChartRef();
                try {
                    const container = chartContainerRefs.current[index]
                    if (container) {
                        chart.resize(container.clientWidth, container.clientHeight);
                    }
                } catch (error) {
                    console.error('Error resizing chart:', error);
                }
            }
        });
    };

    useEffect(() => {
        if (content) {
            const resizeObserver = new ResizeObserver(resizeHandler);
            resizeObserver.observe(content);
            return () => {
                if (resizeObserver) {
                    resizeObserver.disconnect();
                }
            };
        }
    }, [content]);

    useEffect(() => {

        chartRefs.current.forEach((chartRef, index) => {
            if (chartRef && chartRef.getChartRef) {
                // console.log(`Chart ${index + 1} instance:`, chartRef);
                const chart = chartRef.getChartRef();
                const series = chartRef.getSeriesRefs();

                const container = chartContainerRefs.current[0];
                if (container) {
                    // เพิ่มการตกแต่ง container
                    container.style.outline = "2px solid #9b9b9b"; // ขอบสีขาว
                    container.style.borderRadius = "10px"; // ขอบมน
                    container.style.overflow = "hidden"; // ซ่อนส่วนเกิน
                    container.style.boxSizing = "border-box"; // ป้องกันการล้นออกมา
                    container.style.height = "780px"; // กำหนดความสูง

                }
                const width = container.clientWidth;
                const height = container.clientHeight;
                chart.resize(width, height);

                if (series) {
                    const seriesData = series[0]?.data();
                    if (seriesData?.length > 0) {
                        const lastTime = new Date(seriesData[seriesData.length - 1].time).getTime();
                        const firstTime = new Date(seriesData[0].time).getTime();

                        const visibleRangeSize = 50//1 * 24 * 60 * 60 * 1000;
                        const from = Math.max(lastTime - visibleRangeSize, firstTime);
                        const to = lastTime;

                        chart.timeScale().setVisibleLogicalRange({ from, to });
                        chart.timeScale().scrollToPosition(2);
                    }
                }


            }

            return () => {


            }
        });
        setIsReady(true)
    }, [isReady, selectedOption, chartData, chartDataPrice, seriesOptions]); // chartContainerRefs.current, chartRefs.current,

    useEffect(() => {
        const todayString = dayjs().format('YYYY-MM-DD');

    // 2. ตรวจสอบว่าวันที่ใน URL ไม่ใช่วันนี้ หรือมีเงื่อนไขอื่นๆ ที่ไม่ให้เชื่อมต่อ
        if (date !== todayString || !isIntraday || !chartTitle) {
            // ถ้าไม่ใช่วันนี้ หรือเงื่อนไขอื่นไม่ผ่าน ให้หยุดการทำงานทันที
            return;
        }


        const wsUrl = `wss://ideatrade1.com:2096/api/ws/sector_rotation/intraday1min?market=${market}&sector=${encodeURIComponent(sector)}`;
        const ws = new WebSocket(wsUrl);


        ws.onopen = () => console.log('WebSocket Connected');
        ws.onerror = (error) => console.error('WebSocket Error:', error);
        ws.onclose = () => console.log('WebSocket Disconnected');


        ws.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);
                const chartWrapper = chartRefs.current[0];
                const seriesArray = chartWrapper?.getSeriesRefs();


                if (!seriesArray || seriesArray.length === 0) return;
                const mainSeries = seriesArray[0];
                if (!mainSeries) return;


                if (message.status === 'success' && Array.isArray(message.data) && message.data.length > 0) {
                    const oldData = mainSeries.data();
                    if (oldData.length === 0) {
                        const initialData = generateData(message.data, 'datetime', 'cumsum', true);
                        mainSeries.setData(initialData);
                        return;
                    }


                    const newPoints = message.data.map(p => ({
                        datetime: p.datetime,
                        cumsum: parseFloat(p.cumsum)
                    }));


                    const lastNewPoint = newPoints[newPoints.length - 1];
                    const lastNewPointTransformed = generateData([lastNewPoint], 'datetime', 'cumsum', true)[0];
                    const lastNewPointTime = lastNewPointTransformed.time;
                    const lastOldPointTime = oldData[oldData.length - 1].time;


                    if (lastNewPointTime === lastOldPointTime) {
                        let mergedData = oldData.map(point => {
                            const date = new Date(point.time * 1000 - (7 * 60 * 60 * 1000));
                            return { datetime: dayjs(date).format("YYYY-MM-DD HH:mm:ss"), cumsum: point.value };
                        });
                        newPoints.forEach(newPoint => {
                            const idx = mergedData.findIndex(d => d.datetime === newPoint.datetime);
                            if (idx !== -1) { mergedData[idx] = newPoint; } else { mergedData.push(newPoint); }
                        });
                        mergedData.sort((a, b) => dayjs(a.datetime).valueOf() - dayjs(b.datetime).valueOf());


                        const finalData = generateData(mergedData, 'datetime', 'cumsum', true);
                        mainSeries.setData(finalData);
                    } else {
                        mainSeries.update(lastNewPointTransformed);
                    }
                }
            } catch (e) {
                console.error("Error processing WebSocket message:", e);
            }
        };


        return () => {
            if (ws) ws.close();
        };
    }, [isIntraday, selectedOption, chartTitle]);


    useEffect(() => {
        let seriesOptions2 = [
            {
                options: {
                    color: '#82f755',
                    lineWidth: 2,
                    priceLineVisible: false,
                    priceScaleId: "right",
                    title: "flow"
                },
                data: chartData,
            }
        ];

        if (chartDataPrice?.length > 0) {
            seriesOptions2.push({
                options: {
                    color: '#f8c953',
                    lineWidth: 2,
                    priceLineVisible: false,
                    priceScaleId: "left",
                    title: "price"
                },
                data: chartDataPrice,
            });
        }

        setSeriesOptions(seriesOptions2);
    }, [chartData, chartDataPrice]);


    return (
        <Box m="20px">
            <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            flexWrap="wrap"
            >
            <Box flex={{ xs: '1 1 100%', md: '1' }} display="flex" justifyContent="center"></Box>
            <Box flex={{ xs: '1 1 100%', md: '1' }} display="flex" justifyContent="center">
                <Header title=" " subtitle="" />
            </Box>
            </Box>

            <Box
            p="0"
            sx={{
                height: {
                xs: '80vh',
                sm: '85vh',
                md: '90vh',
                lg: '90vh',
                xl: '100vh',
                },
                display: 'flex',
                flexDirection: 'column',
                mt: '-3.3rem',
            }}
            >
            <Container
                maxWidth={false}
                sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                width: { xs: '95vw', sm: '90vw', md: '85vw' },
                mx: 'auto',
                }}
            >
                <Paper
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    p: 2,
                    maxHeight: {
                    xs: '80vh',
                    sm: '80vh',
                    md: '90vh',
                    lg: '90vh',
                    xl: '100vh',
                    },
                }}
                >
                <Grid
                container
                spacing={0} 
                sx={{
                    width: '100%',
                    margin: 0,
                    padding: 0,
                    height: '100%',
                    overflow: 'hidden', 
                }}
                >
                    <Grid
                    item
                    xs={12}
                    md={10}
                    sx={{
                        height: {
                        xs: '30vh', 
                        sm: '40vh',
                        md: '100%',
                        },
                        padding: 1,
                        margin: 0,
                        borderRadius: '4px',
                        display: 'flex', 
                        flexDirection: 'column',
                    }}
                    >
                    <Box
                        sx={{
                        width: '100%',
                        height: '100%',
                        padding: 0,
                        marginTop: '10px',
                        maxHeight: {
                            xs: '30vh',
                            sm: '40vh',
                            md: '80vh',
                            lg: '80vh',
                            xl: '90vh',
                        },
                        }}
                        ref={(el) => (chartContainerRefs.current[0] = el)}
                    >
                        <LightweightChartv1
                        ref={(el) => (chartRefs.current[0] = el)}
                        options={{ ...commonOptions, fontSize: '50px' }}
                        seriesOptions={seriesOptions}
                        title={chartTitle}
                        />
                    </Box>
                    </Grid>

                    <Grid
                    item
                    xs={12}
                    md={2}
                    sx={{
                        maxHeight: '100%',
                        p: 0,
                        m: 0,
                    }}
                    >
                    <Typography
                        variant="h6"
                        sx={{
                        marginLeft: '10px',
                        marginBottom: '10px',
                        fontWeight: 'bold',
                        color: '#f5cd3d',
                        textAlign: { xs: 'center', md: 'left' },
                        }}
                    >
                        Symbol In Sector
                    </Typography>

                    <Box
                        sx={{
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': {
                            width: 8,
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#8b8b8b',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            backgroundColor: '#555',
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: '#f1f1f1',
                        },
                        maxHeight: {
                            xs: '30vh',
                            sm: '40vh',
                            md: '80vh',
                            lg: '80vh',
                            xl: '90vh',
                        },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        }}
                    >
                        {storedSymbols?.map((item, index) => (
                        <Box
                            key={index}
                            sx={{
                            border: '1px solid #000',
                            width: '90%',
                            textAlign: 'center',
                            padding: 1.3,
                            backgroundColor: '#636a7e',
                            borderRadius: '10px',
                            mb: 1,
                            }}
                        >
                            <Typography
                            variant="body1"
                            sx={{
                                fontWeight: 'bold',
                                color: '#fff',
                            }}
                            >
                            {item.Symbol}
                            </Typography>
                        </Box>
                        ))}
                    </Box>
                    </Grid>
                </Grid>
                </Paper>
            </Container>
            </Box>
        </Box>
    );

};

export default FullChart;
